if (!customElements.get('product-quote-cta')) {
  class ProductQuoteCta extends HTMLElement {
    connectedCallback() {
      this.cleanup();

      this.helper = window.TodayMerchQuantityPricing;
      this.data = this.parseData();
      if (!this.data) return;

      this.bound = {
        onQuantityChange: this.handleQuantityChange.bind(this),
        onCartUpdate: this.handleCartUpdate.bind(this),
        onVariantChange: this.handleVariantChange.bind(this),
        onSubmit: this.handleSubmit.bind(this),
      };

      this.refreshBindings();
      this.subscribeToVariantChanges();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      this.resetFeedback();
      this.abortController?.abort();
      this.abortController = undefined;

      if (typeof this.cartUpdateUnsubscriber === 'function') {
        this.cartUpdateUnsubscriber();
      }

      if (typeof this.variantChangeUnsubscriber === 'function') {
        this.variantChangeUnsubscriber();
      }

      this.cartUpdateUnsubscriber = undefined;
      this.variantChangeUnsubscriber = undefined;
      this.bound = undefined;
    }

    parseData() {
      const node = this.querySelector('[data-quote-cta-data]');
      if (!node) return null;

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return null;
      }
    }

    cacheElements() {
      this.button = this.querySelector('[data-role="quote-submit"]');
      const label = this.querySelector('[data-role="quote-submit-label"]');
      if (label !== this.submitLabel) {
        this.submitLabel = label;
        this.originalSubmitLabel = label?.textContent;
      }
      this.pricing = this.querySelector('[data-role="quote-submit-pricing"]');
      this.total = this.querySelector('[data-role="quote-submit-total"]');
      this.unit = this.querySelector('[data-role="quote-submit-unit"]');
      this.successText = this.querySelector('[data-role="quote-success-text"]');
      this.successStatus = this.querySelector('[data-role="quote-success-status"]');
      this.form = document.getElementById(this.data?.productFormId);
      this.sourceInput = this.form?.querySelector('input[name="quantity"]') || null;
      this.variantInput = this.form?.querySelector('input[name="id"]') || null;
    }

    bindEvents() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      if (!this.abortController || !this.bound) return;

      const { signal } = this.abortController;

      this.sourceInput?.addEventListener('input', this.bound.onQuantityChange, { signal });
      this.sourceInput?.addEventListener('change', this.bound.onQuantityChange, { signal });
      this.form?.addEventListener('submit', this.bound.onSubmit, { signal });
      this.closest?.('product-info, .section-main-product')?.addEventListener('supplier-pricing:change', this.bound.onQuantityChange, { signal });
      this.closest?.('product-info, .section-main-product')?.addEventListener('todaymerch:pad-acceptance-change', this.bound.onQuantityChange, { signal });
      this.closest?.('product-info, .section-main-product')?.addEventListener('todaymerch:personalization-orchestrator-ready', this.bound.onQuantityChange, { signal });
      this.button?.addEventListener('click', event => {
        const root = this.closest('product-info');
        const preparation = window.TodayMerchPersonalizationOrchestrator?.get(root);
        const gate = window.TodayMerchPadPreparationGate?.read(root);
        if (gate ? !gate.required || gate.canConfirm : preparation?.canConfirm?.() !== false) return;
        event.preventDefault(); event.stopImmediatePropagation();
        if (gate?.focus) gate.focus(); else preparation?.focusPadPreparation?.();
      }, { signal });

      if (typeof this.cartUpdateUnsubscriber === 'function') {
        this.cartUpdateUnsubscriber();
      }

      this.cartUpdateUnsubscriber = FoxTheme?.pubsub?.subscribe
        ? FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.bound.onCartUpdate)
        : undefined;
    }

    subscribeToVariantChanges() {
      if (!FoxTheme?.pubsub?.subscribe || !this.bound) return;

      this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.bound.onVariantChange
      );
    }

    refreshBindings() {
      this.cacheElements();
      this.bindEvents();
      this.updatePricing();
    }

    handleVariantChange(event) {
      const sectionId = event?.data?.sectionId;
      if (!sectionId || this.data?.productFormId !== `product-form-${sectionId}`) return;
      this.resetFeedback();
      this.refreshBindings();
    }

    handleQuantityChange() {
      this.resetFeedback();
      this.updatePricing();
    }

    handleSubmit() {
      // ProductForm sets loading only when it accepts a new submission.
      if (this.button?.classList.contains('btn--loading')) this.resetFeedback();
    }

    resetFeedback() {
      window.clearTimeout(this.feedbackTimer);
      window.clearTimeout(this.feedbackResetTimer);
      this.feedbackResolve?.();
      this.feedbackResolve = undefined;
      this.button?.classList.remove('is-quote-success');
      if (this.feedbackOriginalLabel !== undefined && this.button) {
        if (this.feedbackOriginalLabel === null) this.button.removeAttribute('aria-label');
        else this.button.setAttribute('aria-label', this.feedbackOriginalLabel);
      }
      this.feedbackOriginalLabel = undefined;
      if (this.successStatus) this.successStatus.textContent = '';
    }

    // Called by this product's form only after its add and cart refresh succeed.
    showAddedFeedback() {
      this.resetFeedback();
      if (!this.isConnected || !this.button || !this.successText) return Promise.resolve();

      const message = this.successText.textContent.trim();
      this.button.style.setProperty('--tm-quote-success-text-width', `${this.successText.scrollWidth}px`);
      this.feedbackOriginalLabel = this.button.getAttribute('aria-label');
      this.button.setAttribute('aria-label', message);
      this.button.setAttribute('aria-busy', 'false');
      this.button.classList.remove('btn--loading');
      this.button.classList.add('is-quote-success');
      if (this.successStatus) this.successStatus.textContent = message;

      // Keep the confirmation readable, then let the existing drawer take over.
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 950;
      this.feedbackResetTimer = window.setTimeout(() => this.resetFeedback(), 2600);
      return new Promise((resolve) => {
        this.feedbackResolve = resolve;
        this.feedbackTimer = window.setTimeout(() => {
          this.feedbackResolve = undefined;
          resolve();
        }, duration);
      });
    }

    handleCartUpdate(response) {
      if (!this.sourceInput) return;

      const cart = response?.cart;
      const variantId = this.variantInput?.value;
      if (!cart || !variantId) return;

      const matchedItem = Array.isArray(cart.items)
        ? cart.items.find((item) => String(item.variant_id) === String(variantId))
        : null;

      const cartQuantity = matchedItem ? Number.parseInt(matchedItem.quantity, 10) || 0 : 0;
      this.sourceInput.setAttribute('data-cart-quantity', String(cartQuantity));
      this.updatePricing();
    }

    getRules() {
      if (this.helper?.getRulesFromAttributes) {
        return this.helper.getRulesFromAttributes(this.sourceInput, {
          min: this.data.defaultQuantity,
          increment: 1,
          max: null,
        });
      }

      const min = Number(this.sourceInput?.getAttribute('data-min') || this.sourceInput?.min || this.data.defaultQuantity || 1);
      const step = Number(this.sourceInput?.getAttribute('step') || this.sourceInput?.step || 1);
      const maxAttr = this.sourceInput?.getAttribute('max') || this.sourceInput?.getAttribute('data-max');
      const max = maxAttr == null || maxAttr === '' ? null : Number(maxAttr);
      return {
        min: Number.isFinite(min) && min > 0 ? min : 1,
        increment: Number.isFinite(step) && step > 0 ? step : 1,
        max: Number.isFinite(max) && max > 0 ? max : null,
      };
    }

    getQuantity() {
      const fallbackValue = this.sourceInput?.value || this.data.defaultQuantity || 1;

      if (this.helper?.normalizeQuantity) {
        return this.helper.normalizeQuantity(fallbackValue, this.getRules(), 'up');
      }

      const parsedQuantity = Number.parseInt(fallbackValue, 10);
      return Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : Number(this.data.defaultQuantity || 1);
    }

    getCartQuantity() {
      const value = this.sourceInput?.getAttribute('data-cart-quantity') || this.sourceInput?.dataset?.cartQuantity || 0;
      return Number.parseInt(value, 10) || 0;
    }

    getNormalizedTiers() {
      if (this.helper?.normalizeTiers) {
        return this.helper.normalizeTiers(this.data.tiers || []);
      }

      return Array.isArray(this.data.tiers) ? this.data.tiers : [];
    }

    computeState() {
      const quantity = this.getQuantity();
      const supplierState = this.closest?.('product-info, .section-main-product')
        ?.querySelector('product-quantity-pricing-selector')?.getSupplierPricingState?.(quantity);
      if (supplierState) return supplierState;
      const tiers = this.getNormalizedTiers();

      if (this.data.mode === 'custom' && tiers.length) {
        const state = this.helper?.computeState
          ? this.helper.computeState({
              quantity,
              tiers,
              rules: this.getRules(),
              quantityMode: 'up',
            })
          : null;

        if (state) {
          return {
            quantity: state.quantity,
            unitPriceCents: state.unitPriceCents,
            totalCents: state.totalCents,
          };
        }
      }

      if (this.data.mode === 'native' && tiers.length) {
        const effectiveQuantity = this.getCartQuantity() + quantity;
        let appliedTier = tiers[0];

        tiers.forEach((tier) => {
          if (tier.qty <= effectiveQuantity) {
            appliedTier = tier;
          }
        });

        return {
          quantity,
          unitPriceCents: appliedTier.unit_price_cents,
          totalCents: quantity * appliedTier.unit_price_cents,
        };
      }

      return {
        quantity,
        unitPriceCents: Number(this.data.defaultUnitPriceCents || 0),
        totalCents: quantity * Number(this.data.defaultUnitPriceCents || 0),
      };
    }

    formatMoney(cents, quantity) {
      const supplierPricing = this.closest?.('product-info, .section-main-product')
        ?.querySelector('product-quantity-pricing-selector')?.supplierPricing;
      const sourceCurrency = supplierPricing ? 'EUR' : this.data?.sourceCurrency || (this.data?.mode === 'custom'
        ? window.TodayMerchMoney?.baseCurrency() || 'EUR'
        : window.TodayMerchMoney?.activeCurrency() || window.FoxTheme?.settings?.currency || 'EUR');
      if (this.helper?.formatMoney) {
        return this.helper.formatMoney(cents, window.FoxTheme?.settings?.moneyFormat, sourceCurrency, quantity);
      }
      if (window.TodayMerchMoney?.format) {
        return window.TodayMerchMoney.format(cents, { sourceCurrency, quantity });
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
        style: 'currency', currency: sourceCurrency,
      }).format(cents / 100);
    }

    updatePricing() {
      const root = this.closest('product-info');
      const preparation = window.TodayMerchPersonalizationOrchestrator?.get(root);
      const gate = window.TodayMerchPadPreparationGate?.read(root);
      const pending = gate ? gate.required && !gate.canConfirm : preparation?.canConfirm?.() === false;
      if (this.submitLabel && !this.button?.disabled) this.submitLabel.textContent = pending
        ? (document.documentElement.lang.startsWith('en') ? 'Confirm your colours' : 'Valider les couleurs')
        : this.submitLabel.dataset.defaultLabel || this.originalSubmitLabel;
      if (!this.pricing || !this.total || !this.unit) return;

      if (pending) {
        this.pricing.hidden = true;
        this.total.textContent = ''; this.unit.textContent = '';
        this.button?.classList.remove('product-form__submit--has-pricing');
        return;
      }

      const state = this.computeState();
      if (state?.supplierStatus && state.supplierStatus !== 'priced') {
        this.pricing.hidden = false;
        this.total.textContent = state.supplierStatus === 'pending' ? 'Calcul…' : state.supplierStatus === 'unavailable' ? 'Indisponible' : 'À confirmer';
        this.unit.textContent = '';
        this.button?.classList.add('product-form__submit--has-pricing');
        return;
      }
      const unitPriceCents = Number(state?.unitPriceCents || 0);
      const totalCents = Number((state?.feeMode === 'separate' ? state.totalPriceCents : state?.totalCents) || 0);

      if (unitPriceCents <= 0 || totalCents <= 0) {
        this.pricing.hidden = true;
        this.button?.classList.remove('product-form__submit--has-pricing');
        return;
      }

      this.pricing.hidden = false;
      this.button?.classList.add('product-form__submit--has-pricing');
      this.total.textContent = this.formatMoney(totalCents, state.quantity);
      const feesIncluded = state?.feeMode === 'separate' && (state.technicalFeesCents > 0 || state.preparationFeesCents > 0);
      this.unit.textContent = feesIncluded ? (document.documentElement.lang.startsWith('en') ? 'Fees included' : 'Frais inclus') : String(this.data?.strings?.unitPrice || '')
        .replace('[money]', this.formatMoney(unitPriceCents))
        .replace('{{ money }}', this.formatMoney(unitPriceCents))
        .replace('{{money}}', this.formatMoney(unitPriceCents));
    }
  }

  customElements.define('product-quote-cta', ProductQuoteCta);
}
